import { useState, useEffect } from 'react';
import axios from 'axios';
import type { User } from './types';
import { Search, SlidersHorizontal, Eye, Ban, CheckCircle2 } from 'lucide-react';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000';

const CARD = "bg-white/[0.04] border border-white/[0.07] rounded-2xl backdrop-blur-sm";
const INPUT = "bg-white/[0.05] border border-white/[0.1] rounded-lg px-3 py-2 text-white text-sm placeholder:text-white/30 focus:outline-none focus:border-primary/50 transition-all";

function UsersManagement() {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [tierFilter, setTierFilter] = useState<string>('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => { fetchUsers(); }, [page, searchTerm, tierFilter]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${API_URL}/api/users`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { page, limit: 20, search: searchTerm, tier: tierFilter }
      });
      setUsers(response.data.users);
      setTotalPages(response.data.totalPages);
    } catch (err) {
      console.error('Failed to fetch users:', err);
    } finally {
      setLoading(false);
    }
  };

  const getTierBadge = (tier: string) => {
    switch (tier) {
      case 'premium': return 'bg-purple/25 text-purple border border-purple/40';
      case 'standard': return 'bg-primary/25 text-primary border border-primary/40';
      default: return 'bg-white/[0.08] text-white/70 border border-white/20';
    }
  };

  const formatDate = (d: string) => new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-white">Users Management</h1>
        <p className="text-white/40 text-sm mt-1">Manage and monitor all registered users</p>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
          <input type="text" placeholder="Search by email or name..." className={`${INPUT} pl-8 w-64`}
            value={searchTerm} onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }} />
        </div>
        <div className="flex items-center gap-2">
          <SlidersHorizontal size={14} className="text-white/30" />
          <select className={INPUT} value={tierFilter} onChange={(e) => { setTierFilter(e.target.value); setPage(1); }}>
            <option value="">All Tiers</option>
            <option value="free">Free</option>
            <option value="standard">Standard</option>
            <option value="premium">Premium</option>
          </select>
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="w-7 h-7 border-2 border-white/10 border-t-primary rounded-full animate-spin" />
        </div>
      ) : (
        <>
          <div className={CARD}>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-white/[0.06]">
                    {['User', 'Email', 'Tier', 'Status', 'Expenses', 'Joined', 'Last Login', 'Actions'].map(h => (
                      <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-white/30 uppercase tracking-wider">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => (
                    <tr key={user.id} className="border-b border-white/[0.04] hover:bg-white/[0.02] transition-colors">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-primary/20 text-primary text-xs font-bold flex items-center justify-center flex-shrink-0">
                            {user.name ? user.name.charAt(0).toUpperCase() : (user.email ? user.email.charAt(0).toUpperCase() : '?')}
                          </div>
                          <span className="text-white/80 text-xs">{user.name || 'N/A'}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-white/50 text-xs">{user.email}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${getTierBadge(user.tier)}`}>
                          {user.tier}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {user.emailVerified ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-green/15 text-green">
                            <CheckCircle2 size={11} /> Verified
                          </span>
                        ) : (
                          <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-orange/15 text-orange">
                            Unverified
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-white/50 text-xs">{user.expense_count}</td>
                      <td className="px-4 py-3 text-white/50 text-xs">{formatDate(user.createdAt)}</td>
                      <td className="px-4 py-3 text-white/50 text-xs">{formatDate(user.lastLogin)}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1.5">
                          <button title="View Details" className="w-7 h-7 rounded-lg bg-primary/15 hover:bg-primary/25 text-primary flex items-center justify-center transition-colors">
                            <Eye size={13} />
                          </button>
                          <button title="Ban User" className="w-7 h-7 rounded-lg bg-red/15 hover:bg-red/25 text-red flex items-center justify-center transition-colors">
                            <Ban size={13} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between">
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
              className="text-sm text-white/50 hover:text-white disabled:opacity-30 transition-colors">
              ← Previous
            </button>
            <span className="text-white/30 text-sm">Page {page} of {totalPages}</span>
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages}
              className="text-sm text-white/50 hover:text-white disabled:opacity-30 transition-colors">
              Next →
            </button>
          </div>
        </>
      )}
    </div>
  );
}

export default UsersManagement;
